import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, Plus, Pencil, Trash2, ArrowUp, ArrowDown, X, Wand2, FlaskConical, Copy, CopyPlus } from "lucide-react";
import { FieldHelp } from "@/components/FieldHelp";
import ComponentWizard from "@/components/ComponentWizard";
import PricingTester from "@/components/PricingTester";

const HELP = {
  key: "A unique identifier for this component (lowercase, e.g. bk_bank_accounts). Used in calculations and dependencies — keep it stable once live.",
  group: "Optional heading to group related questions together under the service on the proposal wizard.",
  label: "The wording shown to the person building the proposal for this question.",
  help: "Optional guidance shown under the question during proposal creation.",
  input: "How the answer is captured: Number, Currency, Yes/No, Dropdown, or Automatic (no question — value is derived).",
  calc: "How this component turns the answer into a fee: fixed amount, quantity × rate, band select, multiplier, percentage, etc.",
  rate: "The amount or rate used by the calculation (e.g. £ per unit, or the % for percentage types).",
  allowance: "Number of units included free before the rate applies. Charge = MAX(0, quantity − allowance) × rate.",
  period: "Monthly feeds the recurring fee; One-off feeds the one-off total (e.g. onboarding, catch-up, fee alignment).",
  applies: "Scope for multipliers/percentages: entire_service, group:Name, components:key,key, or service:internal_name. This changes the result materially.",
  default: "A value pre-filled for this question when a new proposal is built.",
  clientLabel: "Optional friendlier wording shown to the client instead of the internal question label.",
  showClient: "On = the chosen answer can appear under the service on the client proposal & PDF. Off = used for pricing only.",
  condition: "Show/apply this component only when another component's answer matches a rule.",
  formula: "Write a custom expression using other components' keys as variables. yes/no answers = 1/0, dropdowns = the selected option's value, numbers = the entered count. Supports + - * / ( ), comparisons and the functions min, max, round, floor, ceil, abs and IF(condition, then, else).",
  options: "Each option adds an amount (£), applies a multiplier (×) or a percentage (%). For dependent pricing, set an allowance and unit rate per option.",
};

const CALC = [
  ["core_fixed", "Fixed amount when service selected"], ["fixed", "Fixed amount when selected"],
  ["qty_rate", "Quantity × rate"], ["qty_over_allowance", "Quantity above allowance × rate"],
  ["tiered", "Tiered quantity"], ["option_amount", "Selected option amount"],
  ["conditional_fixed", "Conditional fixed amount"], ["multiplier", "Multiplier on subtotal"],
  ["increase_pct", "Percentage increase"], ["reduce_pct", "Percentage reduction"],
  ["qty_multiplier", "Quantity × subtotal (e.g. catch-up months)"], ["min_floor", "Minimum / floor"],
  ["manual_adjust", "Fixed manual adjustment"], ["alignment_months", "Elapsed months × target monthly fee"],
  ["plan_per_unit_extra", "Dependent plan: qty above option allowance × option rate"],
  ["plan_pct_uplift", "Renewal uplift: apply the chosen option's % increase"],
  ["formula", "Formula (advanced) — custom cross-component expression"],
];
const INPUT = [["number", "Number"], ["currency", "Currency"], ["yes_no", "Yes / No"], ["dropdown", "Dropdown"],
  ["service_selection", "Service selection"], ["automatic", "Automatic / hidden"]];
const HAS_OPTIONS = ["dropdown", "tiered", "multiplier", "increase_pct", "reduce_pct"];
const BLANK = () => ({ key: "", name: "", label: "", group: "", help_text: "", input_type: "number",
  calc_type: "fixed", rate: 0, required: false, default: "", order: 999, active: true, period: "monthly",
  applies_to: "entire_service", allowance: 0, options: [], condition: null, depends_on: "" });

export default function PricingComponents() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [svc, setSvc] = useState(null);
  const [comps, setComps] = useState([]);
  const [edit, setEdit] = useState(null);
  const [wizard, setWizard] = useState(false);
  const [tester, setTester] = useState(null);
  const [copyOpen, setCopyOpen] = useState(false);
  const [allServices, setAllServices] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [depKeys, setDepKeys] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => {
      const s = r.data.find((x) => x.id === id);
      setSvc(s);
      setAllServices(r.data || []);
      setComps([...((s?.pricing_v2 || {}).components || [])].sort((a, b) => (a.order ?? 999) - (b.order ?? 999)));
      const keys = [];
      (r.data || []).forEach((x) => ((x.pricing_v2 || {}).components || []).forEach((c) =>
        keys.push({ key: c.key, label: `${c.key} — ${c.label || c.name || ""} (${x.client_name})` })));
      setDepKeys(keys.filter((k, i, a) => a.findIndex((z) => z.key === k.key) === i));
    });
  }, [id]);

  const configError = (c) => {
    if (c.calc_type !== "plan_per_unit_extra") return null;
    if (!c.depends_on || !String(c.depends_on).trim()) return "No 'Depends on' component set";
    if (String(c.depends_on).trim() === String(c.key).trim()) return "Cannot depend on itself";
    if (depKeys.length && !depKeys.some((k) => k.key === String(c.depends_on).trim()))
      return `Dependent component '${c.depends_on}' not found`;
    const local = comps.find((x) => x.key === String(c.depends_on).trim());
    if (local && !(local.options || []).some((o) => o.allowance != null && o.rate != null))
      return `Options of '${c.depends_on}' need allowance & rate`;
    return null;
  };

  const save = async () => {
    const keys = comps.map((c) => c.key.trim());
    if (keys.some((k) => !k)) return toast.error("Every component needs a key");
    if (new Set(keys).size !== keys.length) return toast.error("Component keys must be unique");
    for (const c of comps.filter((x) => x.calc_type === "plan_per_unit_extra")) {
      if (!c.depends_on || !String(c.depends_on).trim())
        return toast.error(`"${c.label || c.key}" is a dependent-plan component but has no "Depends on" component set`);
      if (String(c.depends_on).trim() === String(c.key).trim())
        return toast.error(`"${c.key}" cannot depend on itself`);
      const local = comps.find((x) => x.key === String(c.depends_on).trim());
      if (local && !(local.options || []).some((o) => o.allowance != null && o.rate != null))
        return toast.error(`Dependent component "${c.depends_on}" needs an Allowance and Rate on its options`);
    }
    const ordered = comps.map((c, i) => ({ ...c, order: i + 1 }));
    try {
      await api.put(`/services/${id}`, { pricing_v2: { components: ordered } });
      setComps(ordered); setDirty(false);
      toast.success("Pricing components saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const move = (i, d) => {
    const g = comps[i].group || "General";
    // Reorder only within the same display group; no-op at the group's top/bottom.
    const idxs = comps.map((c, x) => (((c.group || "General") === g) ? x : -1)).filter((x) => x >= 0);
    const pos = idxs.indexOf(i);
    const swap = pos + d;
    if (swap < 0 || swap >= idxs.length) return;
    const a = idxs[pos], b = idxs[swap];
    const n = [...comps]; [n[a], n[b]] = [n[b], n[a]]; setComps(n); setDirty(true);
  };
  const removeComp = (i) => { setComps(comps.filter((_, x) => x !== i)); setDirty(true); };
  const upsert = (c) => {
    const idx = edit._idx;
    if (idx >= 0 && idx < comps.length) { const n = [...comps]; n[idx] = c; setComps(n); }
    else setComps([...comps, c]);
    setDirty(true); setEdit(null);
  };

  const uniqueKey = (base, keys) => {
    if (!keys.includes(base)) return base;
    let n = 2; while (keys.includes(`${base}${n}`)) n++; return `${base}${n}`;
  };
  const duplicate = (i) => {
    const src = JSON.parse(JSON.stringify(comps[i]));
    const key = uniqueKey(`${src.key}_copy`, comps.map((x) => x.key));
    const clone = { ...src, key, status: "draft",
      label: `${src.label || src.name || src.key} (copy)`, name: `${src.name || src.label || src.key} (copy)` };
    const n = [...comps]; n.splice(i + 1, 0, clone); setComps(n); setDirty(true);
    toast.success("Duplicated as draft — review and Save changes");
  };
  const toggleDraft = (i) => {
    const n = [...comps]; n[i] = { ...n[i], status: n[i].status === "draft" ? undefined : "draft" };
    setComps(n); setDirty(true);
  };
  const copyFrom = (srcId, mode) => {
    const src = allServices.find((s) => s.id === srcId);
    const srcComps = (src?.pricing_v2 || {}).components || [];
    if (!srcComps.length) { toast.error("That service has no pricing components"); return; }
    if (mode === "replace") {
      setComps(srcComps.map((c, i) => ({ ...JSON.parse(JSON.stringify(c)), status: "draft", order: i + 1 })));
    } else {
      const keys = comps.map((c) => c.key);
      const added = [];
      srcComps.forEach((c) => {
        const key = uniqueKey(keys.includes(c.key) ? `${c.key}_copy` : c.key, keys);
        keys.push(key);
        added.push({ ...JSON.parse(JSON.stringify(c)), key, status: "draft" });
      });
      setComps([...comps, ...added.map((c, i) => ({ ...c, order: comps.length + i + 1 }))]);
    }
    setDirty(true); setCopyOpen(false);
    toast.success(`Copied ${srcComps.length} components from ${src.client_name} as drafts — review and Save`);
  };

  const groups = [...new Set(comps.map((c) => c.group || "General"))];

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="pricing-components-page">
      <Button variant="ghost" size="sm" onClick={() => navigate("/services")} className="mb-2"><ChevronLeft className="w-4 h-4 mr-1" />Back to catalogue</Button>
      <Button variant="outline" size="sm" onClick={() => navigate("/pricing-help")} className="mb-2 ml-2" data-testid="pricing-help-link">Pricing Help Centre</Button>
      <PageHeader title={`Pricing components — ${svc?.client_name || ""}`} subtitle="Selection-driven pricing. Tune components, options, scope and conditions with no code changes." />
      <div className="flex justify-between items-center my-4">
        <p className="text-sm text-cass-slate" data-testid="components-count">{comps.length} components · {groups.length} groups</p>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setTester({ focusKey: null })} data-testid="test-service-btn"><FlaskConical className="w-4 h-4 mr-1" />Test pricing</Button>
          <Button variant="outline" onClick={() => setCopyOpen(true)} data-testid="copy-from-btn"><CopyPlus className="w-4 h-4 mr-1" />Copy from…</Button>
          <Button variant="outline" onClick={() => setWizard(true)} data-testid="wizard-btn"><Wand2 className="w-4 h-4 mr-1" />Create with wizard</Button>
          <Button variant="outline" onClick={() => setEdit({ ...BLANK(), _idx: -1 })} data-testid="add-component-btn"><Plus className="w-4 h-4 mr-1" />Add component</Button>
          <Button className="bg-cass-navy text-white" onClick={save} disabled={!dirty} data-testid="save-components-btn">Save changes</Button>
        </div>
      </div>

      {groups.map((g) => (
        <div key={g} className="mb-5">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-cass-slate/70 mb-2">{g}</h3>
          <div className="space-y-2">
            {comps.map((c, i) => ((c.group || "General") === g) && (
              <Card key={i} className={`p-3 flex items-center justify-between ${c.active === false ? "opacity-50" : ""} ${c.status === "draft" ? "border-amber-400 bg-amber-50/40" : ""}`} data-testid={`component-row-${c.key}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-cass-navy truncate">{c.label || c.name || c.key}</span>
                    <Badge variant="outline" className="text-[10px]">{c.calc_type}</Badge>
                    <Badge variant="outline" className="text-[10px]">{c.period}</Badge>
                    {c.required && <Badge className="text-[10px] bg-cass-bronze">required</Badge>}
                    {c.status === "draft" && <Badge className="text-[10px] bg-amber-500" data-testid={`draft-badge-${c.key}`}>draft</Badge>}
                  </div>
                  <p className="text-xs text-cass-slate truncate">{c.key} · {c.input_type} · applies: {c.applies_to}{c.rate ? ` · £${c.rate}` : ""}{c.options?.length ? ` · ${c.options.length} options` : ""}</p>
                  {configError(c) && <p className="text-xs text-red-600 font-medium mt-0.5" data-testid={`config-warning-${c.key}`}>⚠ {configError(c)}</p>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="ghost" className="text-[10px] h-7 px-2 text-amber-700" onClick={() => toggleDraft(i)} data-testid={`draft-toggle-${c.key}`}>{c.status === "draft" ? "Activate" : "Set draft"}</Button>
                  <Button size="icon" variant="ghost" onClick={() => setTester({ focusKey: c.key })} title="Test this component" data-testid={`test-${c.key}`}><FlaskConical className="w-4 h-4 text-cass-teal" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => duplicate(i)} title="Duplicate" data-testid={`dup-${c.key}`}><Copy className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => move(i, -1)} data-testid={`up-${c.key}`}><ArrowUp className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => move(i, 1)} data-testid={`down-${c.key}`}><ArrowDown className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => setEdit({ ...c, _idx: i })} data-testid={`edit-${c.key}`}><Pencil className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => removeComp(i)} data-testid={`del-${c.key}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      ))}

      {edit && <ComponentDialog value={edit} depKeys={depKeys} siblingKeys={comps.map((c) => ({ key: c.key, label: c.label || c.name || c.key }))} onClose={() => setEdit(null)} onSave={upsert} />}
      {wizard && <ComponentWizard existingKeys={comps.map((c) => c.key)} internalName={svc?.internal_name || ""} onClose={() => setWizard(false)} onCreate={(c) => { setComps([...comps, { ...c, order: comps.length + 1 }]); setDirty(true); toast.success("Component added — review and Save changes"); }} />}
      {tester && <PricingTester serviceName={svc?.client_name || ""} components={comps} focusKey={tester.focusKey} onClose={() => setTester(null)} />}
      {copyOpen && <CopyFromDialog services={allServices.filter((s) => s.id !== id && ((s.pricing_v2 || {}).components || []).length)} onClose={() => setCopyOpen(false)} onCopy={copyFrom} />}
    </div>
  );
}

function ComponentDialog({ value, depKeys = [], siblingKeys = [], onClose, onSave }) {
  const [c, setC] = useState({ ...value });
  const [fx, setFx] = useState(null);
  const set = (k, v) => setC({ ...c, [k]: v });
  const showOptions = HAS_OPTIONS.includes(c.input_type) || HAS_OPTIONS.includes(c.calc_type);
  const setOpt = (i, k, v) => { const o = [...(c.options || [])]; o[i] = { ...o[i], [k]: v }; set("options", o); };
  const addOpt = () => set("options", [...(c.options || []), { key: "", name: "", value: 0, value_type: "£", order: (c.options?.length || 0) + 1, active: true }]);
  const delOpt = (i) => set("options", c.options.filter((_, x) => x !== i));
  const moveOpt = (i, dir) => {
    const o = [...(c.options || [])];
    const j = i + dir;
    if (j < 0 || j >= o.length) return;
    [o[i], o[j]] = [o[j], o[i]];
    set("options", o.map((x, idx) => ({ ...x, order: idx + 1 })));
  };
  const cond = c.condition?.conditions?.[0] || { factor: "", op: "yes", value: "" };
  const setCond = (patch) => {
    const nc = { ...cond, ...patch };
    set("condition", nc.factor ? { match: "all", conditions: [nc] } : null);
  };
  const insertVar = (k) => {
    const f = c.formula || "";
    const sep = f && !/[\s(+\-*/]$/.test(f) ? " " : "";
    set("formula", f + sep + k);
    setFx(null);
  };
  const checkFormula = async () => {
    try {
      const { data } = await api.post("/pricing/validate-formula", { formula: c.formula || "" });
      setFx(data);
    } catch {
      setFx({ ok: false, error: "Could not validate" });
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="component-dialog">
        <DialogHeader><DialogTitle>{value._idx >= 0 ? "Edit" : "Add"} component</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Component key *<FieldHelp text={HELP.key} testid="help-key" /></Label><Input value={c.key} onChange={(e) => set("key", e.target.value)} data-testid="comp-key" /></div>
          <div><Label className="text-xs">Display group<FieldHelp text={HELP.group} testid="help-group" /></Label><Input value={c.group} onChange={(e) => set("group", e.target.value)} data-testid="comp-group" /></div>
          <div className="col-span-2"><Label className="text-xs">Proposal question / label<FieldHelp text={HELP.label} testid="help-label" /></Label><Input value={c.label} onChange={(e) => set("label", e.target.value)} data-testid="comp-label" /></div>
          <div className="col-span-2"><Label className="text-xs">Help text<FieldHelp text={HELP.help} testid="help-helptext" /></Label><Input value={c.help_text} onChange={(e) => set("help_text", e.target.value)} data-testid="comp-help" /></div>
          <div><Label className="text-xs">Input type<FieldHelp text={HELP.input} testid="help-input" /></Label>
            <Select value={c.input_type} onValueChange={(v) => set("input_type", v)}><SelectTrigger data-testid="comp-input"><SelectValue /></SelectTrigger>
              <SelectContent>{INPUT.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">Calculation type<FieldHelp text={HELP.calc} testid="help-calc" /></Label>
            <Select value={c.calc_type} onValueChange={(v) => set("calc_type", v)}><SelectTrigger data-testid="comp-calc"><SelectValue /></SelectTrigger>
              <SelectContent>{CALC.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">Rate / amount<FieldHelp text={HELP.rate} testid="help-rate" /></Label><Input type="number" value={c.rate} onChange={(e) => set("rate", parseFloat(e.target.value) || 0)} data-testid="comp-rate" /></div>
          <div><Label className="text-xs">Allowance (for above-allowance)<FieldHelp text={HELP.allowance} testid="help-allowance" /></Label><Input type="number" value={c.allowance} onChange={(e) => set("allowance", parseFloat(e.target.value) || 0)} data-testid="comp-allowance" /></div>
          <div><Label className="text-xs">Pricing period<FieldHelp text={HELP.period} testid="help-period" /></Label>
            <Select value={c.period} onValueChange={(v) => set("period", v)}><SelectTrigger data-testid="comp-period"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="one_off">One-off</SelectItem></SelectContent></Select></div>
          <div><Label className="text-xs">Applies to (scope)<FieldHelp text={HELP.applies} testid="help-applies" /></Label>
            <Input value={c.applies_to} onChange={(e) => set("applies_to", e.target.value)} placeholder="entire_service | group:Name | components:key,key | service:internal_name" data-testid="comp-applies" /></div>
          <div><Label className="text-xs">Default value<FieldHelp text={HELP.default} testid="help-default" /></Label><Input value={c.default ?? ""} onChange={(e) => set("default", e.target.value)} data-testid="comp-default" /></div>
          <div className="col-span-2"><Label className="text-xs">Client proposal label (optional — shown to the client instead of the question)<FieldHelp text={HELP.clientLabel} testid="help-client-label" /></Label><Input value={c.client_label || ""} onChange={(e) => set("client_label", e.target.value)} placeholder={c.label || ""} data-testid="comp-client-label" /></div>
          <div className="flex items-end gap-4 flex-wrap">
            <label className="flex items-center gap-2 text-sm"><Switch checked={!!c.required} onCheckedChange={(v) => set("required", v)} data-testid="comp-required" />Required</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={c.active !== false} onCheckedChange={(v) => set("active", v)} data-testid="comp-active" />Active</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={c.status === "draft"} onCheckedChange={(v) => set("status", v ? "draft" : undefined)} data-testid="comp-draft" />Draft (staged)<FieldHelp text="Draft components are staged and testable but are excluded from live proposals until you activate them." testid="help-draft" /></label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={c.show_client == null ? !["multiplier", "manual_adjust", "plan_pct_uplift"].includes(c.calc_type) : !!c.show_client} onCheckedChange={(v) => set("show_client", v)} data-testid="comp-show-client" />Show on client proposal<FieldHelp text={HELP.showClient} testid="help-show-client" /></label>
          </div>
        </div>

        {c.calc_type === "plan_per_unit_extra" && (
          <div className="border-t pt-3">
            <Label className="text-xs">Depends on component — its selected option supplies the allowance &amp; unit rate</Label>
            <Input list="dep-keys" value={c.depends_on || ""} onChange={(e) => set("depends_on", e.target.value)} placeholder="e.g. software_xero (may be on another service)" data-testid="comp-depends-on" />
            <datalist id="dep-keys">{depKeys.filter((k) => k.key !== c.key).map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}</datalist>
            <p className="text-[11px] text-cass-slate mt-1">Set an <b>Allowance</b> and <b>Rate</b> on each option of that component. Charge = MAX(0, quantity − allowance) × rate.</p>
          </div>
        )}

        {c.calc_type === "plan_pct_uplift" && (
          <div className="border-t pt-3">
            <Label className="text-xs">Uplift effective from (optional) — leave blank to apply immediately</Label>
            <Input type="date" value={c.effective_from || ""} onChange={(e) => set("effective_from", e.target.value)} data-testid="comp-effective-from" />
            <p className="text-[11px] text-cass-slate mt-1">Put the % on each option (its <b>rate</b>). With a date set, the current price is unchanged and the proposal shows the post-renewal price from that date.</p>
          </div>
        )}

        {c.calc_type === "formula" && (
          <div className="border-t pt-3" data-testid="formula-editor">
            <Label className="text-xs">Formula (advanced)<FieldHelp text={HELP.formula} testid="help-formula" /></Label>
            <textarea
              className="w-full border border-input rounded-md p-2 text-sm font-mono mt-1 focus:outline-none focus:ring-1 focus:ring-cass-teal"
              rows={2}
              value={c.formula || ""}
              onChange={(e) => { set("formula", e.target.value); setFx(null); }}
              placeholder="e.g. bk_bank_accounts * 25 + IF(bk_complexity > 1, 50, 0)"
              data-testid="comp-formula"
            />
            <p className="text-[11px] text-cass-slate mt-1">
              Insert a component:{" "}
              {siblingKeys.filter((k) => k.key !== c.key).map((k) => (
                <button key={k.key} type="button" onClick={() => insertVar(k.key)}
                  className="inline-block bg-cass-teal/10 text-cass-teal rounded px-1.5 py-0.5 mr-1 mb-1 text-[11px] hover:bg-cass-teal/20"
                  title={k.label} data-testid={`formula-var-${k.key}`}>{k.key}</button>
              ))}
              {siblingKeys.length <= 1 && <span className="italic">no other components yet</span>}
            </p>
            <p className="text-[11px] text-cass-slate">Functions: min, max, round, floor, ceil, abs, IF(condition, then, else). Operators: + − × ÷ ( ) and comparisons &lt; &gt; == with IF.</p>
            <div className="flex items-center gap-2 mt-1">
              <Button size="sm" variant="outline" onClick={checkFormula} data-testid="formula-check">Check formula</Button>
              {fx && (fx.ok
                ? <span className="text-xs text-green-600" data-testid="formula-result">✓ Valid — sample result £{fx.value} (all variables set to 1)</span>
                : <span className="text-xs text-red-600" data-testid="formula-error">✗ {fx.error}</span>)}
            </div>
          </div>
        )}

        <div className="border-t pt-3 mt-1">
          <Label className="text-xs">Condition — show only when another component's answer matches (optional)<FieldHelp text={HELP.condition} testid="help-condition" /></Label>
          <div className="flex gap-2 mt-1">
            <Input placeholder="component key" value={cond.factor} onChange={(e) => setCond({ factor: e.target.value })} data-testid="comp-cond-factor" />
            <Select value={cond.op} onValueChange={(v) => setCond({ op: v })}><SelectTrigger className="w-32" data-testid="comp-cond-op"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="yes">is Yes</SelectItem><SelectItem value="no">is No</SelectItem><SelectItem value="eq">equals</SelectItem><SelectItem value="gt">&gt;</SelectItem><SelectItem value="lt">&lt;</SelectItem></SelectContent></Select>
            <Input placeholder="value" value={cond.value} onChange={(e) => setCond({ value: e.target.value })} data-testid="comp-cond-value" />
          </div>
        </div>

        {showOptions && (
          <div className="border-t pt-3">
            <div className="flex justify-between items-center mb-2"><Label className="text-xs">Option values (£ adds an amount, × applies a multiplier)<FieldHelp text={HELP.options} testid="help-options" /></Label>
              <Button size="sm" variant="outline" onClick={addOpt} data-testid="add-option-btn"><Plus className="w-3 h-3 mr-1" />Option</Button></div>
            {(c.options || []).map((o, i) => (
              <div key={i} className="flex gap-1.5 mb-1 items-center" data-testid={`option-row-${i}`}>
                <Input className="flex-1 min-w-0" placeholder="Option name" value={o.name} onChange={(e) => setOpt(i, "name", e.target.value)} />
                <Input className="w-24" placeholder="key" value={o.key} onChange={(e) => setOpt(i, "key", e.target.value)} />
                <Input className="w-20" type="number" placeholder="value" value={o.value} onChange={(e) => setOpt(i, "value", parseFloat(e.target.value) || 0)} />
                <Select value={o.value_type || "£"} onValueChange={(v) => setOpt(i, "value_type", v)}><SelectTrigger className="w-14"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="£">£</SelectItem><SelectItem value="×">×</SelectItem><SelectItem value="%">%</SelectItem></SelectContent></Select>
                <Input className="w-16" type="number" placeholder="alw" title="Free allowance (dependent pricing)" value={o.allowance ?? ""} onChange={(e) => setOpt(i, "allowance", e.target.value === "" ? undefined : parseFloat(e.target.value))} data-testid={`option-alw-${i}`} />
                <Input className="w-16" type="number" placeholder="rate" title="Unit rate (dependent pricing)" value={o.rate ?? ""} onChange={(e) => setOpt(i, "rate", e.target.value === "" ? undefined : parseFloat(e.target.value))} data-testid={`option-rate-${i}`} />
                <Input className="w-28" placeholder="client text" title="Client display value (optional)" value={o.client_display ?? ""} onChange={(e) => setOpt(i, "client_display", e.target.value)} data-testid={`option-client-${i}`} />
                <Button size="icon" variant="ghost" title="Move up" disabled={i === 0} onClick={() => moveOpt(i, -1)} data-testid={`option-up-${i}`}><ArrowUp className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" title="Move down" disabled={i === (c.options || []).length - 1} onClick={() => moveOpt(i, 1)} data-testid={`option-down-${i}`}><ArrowDown className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => delOpt(i)}><X className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="bg-cass-navy text-white" onClick={() => onSave({ ...c, name: c.name || c.label || c.key })} data-testid="comp-save">Save component</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function CopyFromDialog({ services = [], onClose, onCopy }) {
  const [srcId, setSrcId] = useState("");
  const src = services.find((s) => s.id === srcId);
  const count = src ? ((src.pricing_v2 || {}).components || []).length : 0;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="copy-from-dialog">
        <DialogHeader><DialogTitle>Copy pricing structure from another service</DialogTitle></DialogHeader>
        <p className="text-sm text-cass-slate">Bring another service's components in as <b>drafts</b> so you can tweak and test them before activating. Live proposals are unaffected until you activate.</p>
        <div className="mt-2">
          <Label className="text-xs">Source service</Label>
          <Select value={srcId} onValueChange={setSrcId}>
            <SelectTrigger data-testid="copy-source"><SelectValue placeholder="Choose a service…" /></SelectTrigger>
            <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.client_name} ({((s.pricing_v2 || {}).components || []).length})</SelectItem>)}</SelectContent>
          </Select>
          {src && <p className="text-xs text-cass-teal mt-1">{count} components will be copied as drafts.</p>}
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="outline" onClick={() => onCopy(srcId, "append")} disabled={!srcId} data-testid="copy-append">Append as drafts</Button>
          <Button className="bg-cass-navy text-white" onClick={() => onCopy(srcId, "replace")} disabled={!srcId} data-testid="copy-replace">Replace all with drafts</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
